import { serializeComposerDraft } from './composer-draft-storage';

/** Capture at most once per interval; unchanged snapshots never rewrite storage. */
export function createComposerDraftWriter(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  key: string,
  delay = 250
) {
  let latest: (() => unknown) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let accepted: string | null = null;
  try {
    accepted = storage.getItem(key);
  } catch {
    /* Storage may be unavailable. */
  }
  const flush = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (!latest) return;
    const capture = latest;
    latest = null;
    try {
      const serialized = serializeComposerDraft(capture());
      if (serialized === accepted) return;
      storage.setItem(key, serialized);
      accepted = serialized;
    } catch {
      // Preserve the latest snapshot for pagehide/unmount or a later edit retry.
      latest = capture;
    }
  };
  return {
    schedule(capture: () => unknown) {
      latest = capture;
      // A maximum interval prevents streaming UI renders from starving persistence.
      timer ??= setTimeout(flush, delay);
    },
    flush,
    cancel() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      latest = null;
    },
  };
}

const activeWriters = new Map<
  string,
  { flush: () => void; retire: () => void }
>();
/** Navigation checkpoints must include any snapshot still waiting for its timer. */
export function registerComposerDraftWriter(
  key: string,
  flush: () => void,
  retire: () => void = () => {}
) {
  // Exit animations can keep the previous composer mounted during a handoff.
  const previous = activeWriters.get(key);
  previous?.flush();
  previous?.retire();
  const owner = { flush, retire };
  activeWriters.set(key, owner);
  return () => {
    if (activeWriters.get(key) === owner) activeWriters.delete(key);
  };
}
export function flushComposerDraftWriter(key: string) {
  activeWriters.get(key)?.flush();
}

/**
 * The draft for `key` was sent: stop its writer so the composer's unmount
 * flush can't persist the sent text again (e.g. /design navigating away
 * while its editor still shows the prompt).
 */
export function retireComposerDraftWriter(key: string) {
  const owner = activeWriters.get(key);
  if (!owner) return;
  owner.retire();
  activeWriters.delete(key);
}
